import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api/axiosConfig";
import endpoints from "../api/endpoints";
import Breadcrumb from "../components/Breadcrumb";

const PAGE_SIZE = 9;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function CurrentAffairs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Math.max(
    parseInt(searchParams.get("page") || "1", 10) || 1,
    1
  );
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(requestedPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadArticles() {
      setLoading(true);
      try {
        const res = await api.get(endpoints.CURRENT_AFFAIRS, {
          params: { page: requestedPage, limit: PAGE_SIZE },
        });
        if (cancelled) return;
        setArticles(res.data.articles || []);
        setPage(res.data.page || requestedPage);
        setTotalPages(res.data.totalPages || 0);
      } catch {
        if (!cancelled) {
          setArticles([]);
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadArticles();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      cancelled = true;
    };
  }, [requestedPage]);

  const goToPage = (nextPage) => {
    const next = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (next === requestedPage) return;
    if (next <= 1) {
      setSearchParams({});
      return;
    }
    setSearchParams({ page: String(next) });
  };

  return (
    <>
      <Breadcrumb title="Current Affairs" path="Current Affairs" />

      <section className="blog-section">
        <div className="container">
          <h2 className="blog-section-title">Latest Current Affairs</h2>

          {loading && (
            <div className="empty-state">
              <p>Loading current affairs...</p>
            </div>
          )}

          {!loading && articles.length === 0 && (
            <div className="empty-state">
              <p>No current affairs found.</p>
            </div>
          )}

          {!loading && articles.length > 0 && (
            <div className="row g-4">
              {articles.map((article) => (
                <div className="col-lg-4 col-md-6" key={article._id || article.id}>
                  <article className="blog-card h-100">
                    <div className="blog-content">
                      <p className="single-blog-meta">{formatDate(article.date)}</p>
                      <h5 className="card-title">{article.heading}</h5>
                      <p className="card-text">{article.excerpt}</p>
                      <Link
                        to={`/current-affairs/${article.id}`}
                        className="theme-btn-sm"
                      >
                        Read More →
                      </Link>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <nav className="blog-pagination" aria-label="Current affairs pages">
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>

              {pageItems(page, totalPages).map((item) =>
                typeof item === "string" ? (
                  <span key={item} className="page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={item === page ? "active" : undefined}
                    onClick={() => goToPage(item)}
                    aria-current={item === page ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default CurrentAffairs;
